import { toYmdSlash } from '../../../utils/datetime';

// クイックタスク表示まわりの共有ユーティリティ。
// AdminTaskHome / QuickTaskPanel / QuickTaskItem から再利用する。

export function normalizeTaskPriority(priority) {
  const key = String(priority || '').trim().toLowerCase();
  if (key === 'low' || key === 'high' || key === 'urgent') return key;
  return 'normal';
}

export function getTaskPriorityClass(priority) {
  return `is-priority-${normalizeTaskPriority(priority)}`;
}

const SOURCE_TYPE_LABELS = {
  chat_artist: 'アーティストチャット',
  chat_event: 'イベントチャット',
  chat_group: 'グループチャット',
  event: 'イベント',
  artist: 'アーティスト',
  contract: '本契約',
  offer: 'オファー',
  quick: 'シンプルタスク',
};

const UNLINKED_SOURCE_TYPES = new Set(['', 'manual', 'mobile_manual', 'mobile_shake', 'quick']);

function compactText(value, max = 56) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1))}…`;
}

export function getQuickTaskSourceTypeLabel(taskOrSource) {
  const source = taskOrSource?.source || taskOrSource || {};
  const type = String(source?.type || '').trim();
  return SOURCE_TYPE_LABELS[type] || SOURCE_TYPE_LABELS.quick;
}

export function formatQuickTaskSourceLabel(task) {
  const source = task?.source || {};
  const type = String(source?.type || '').trim();
  const typeLabel = getQuickTaskSourceTypeLabel(source);
  const title = compactText(source?.title || source?.name || '', 52);
  const excerpt = compactText(source?.excerpt || source?.messageText || '', 46);

  if (title && excerpt && /^chat_/.test(type)) return `${typeLabel}: ${title} / ${excerpt}`;
  if (title) return `${typeLabel}: ${title}`;
  if (excerpt) return `${typeLabel}: ${excerpt}`;
  if (UNLINKED_SOURCE_TYPES.has(type)) return '';
  return typeLabel;
}

export function getQuickTaskSourceDetail(task, options = {}) {
  const includeInternalIds = options.includeInternalIds === true;
  const source = task?.source || {};
  const typeLabel = getQuickTaskSourceTypeLabel(source);
  const title = compactText(source?.title || source?.name || '', 90);
  const excerpt = compactText(source?.excerpt || source?.messageText || '', 160);
  const rows = [];

  if (!title && !excerpt && UNLINKED_SOURCE_TYPES.has(String(source?.type || '').trim())) {
    return {
      typeLabel: 'シンプルタスク',
      title: '',
      excerpt: '',
      rows,
    };
  }

  rows.push({ label: '連動元', value: typeLabel });

  if (title) {
    const sourceType = String(source?.type || '').trim();
    let titleLabel = '対象';
    if (sourceType === 'event') titleLabel = 'イベント';
    if (/^chat_/.test(sourceType)) titleLabel = 'チャット';
    rows.push({ label: titleLabel, value: title });
  }

  if (excerpt) {
    const isChatSource = /^chat_/.test(String(source?.type || '').trim());
    rows.push({ label: isChatSource ? '元メッセージ' : '補足', value: excerpt });
  }

  if (includeInternalIds) {
    const eventUuid = String(source?.eventUuid || source?.event_uuid || '').trim();
    const artistId = String(source?.artistId || source?.artist_id || '').trim();
    const eventName = compactText(
      source?.eventName ||
        source?.event_name ||
        task?.linkedEventName ||
        task?.linked_event_name ||
        '',
      90
    );
    const artistName = compactText(
      source?.artistName ||
        source?.artist_name ||
        task?.linkedArtistName ||
        task?.linked_artist_name ||
        '',
      90
    );
    const messageId = String(source?.messageId || source?.message_id || '').trim();
    if (eventUuid) {
      rows.push({
        label: 'イベント',
        value: eventName || 'イベント詳細',
        href: `/admin/workspaces/event-operations/events/${encodeURIComponent(eventUuid)}`,
      });
    }
    if (artistId) {
      rows.push({
        label: 'アーティスト',
        value: artistName || 'アーティスト詳細',
        href: `/admin/workspaces/artist-management/artists/${encodeURIComponent(artistId)}`,
      });
    }
    if (messageId) rows.push({ label: 'メッセージID', value: messageId });
  }

  return {
    typeLabel,
    title: title || typeLabel,
    excerpt,
    rows,
  };
}

export function getQuickTaskRequester(task) {
  const user = task?.requestedByUser || task?.requested_by_user || null;
  const displayName = String(
    user?.displayName ||
      user?.display_name ||
      task?.requestedBy ||
      task?.requested_by ||
      ''
  ).trim();
  if (!displayName) return null;
  return {
    type: 'admin',
    id: user?.id || user?.adminUserId || user?.admin_user_id || task?.requestedByAdminId || '',
    displayName,
    profilePicture: String(
      user?.profilePicture ||
        user?.profile_picture ||
        task?.requestedByProfilePicture ||
        task?.requested_by_profile_picture ||
        ''
    ).trim(),
  };
}

function toDateOnly(value) {
  if (!value) return null;
  const text = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const date = new Date(`${text}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateKey(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function diffDays(a, b) {
  const oneDayMs = 24 * 60 * 60 * 1000;
  return Math.floor((b.getTime() - a.getTime()) / oneDayMs);
}

function formatDueLabel(value) {
  return value ? toYmdSlash(value) || '期限未設定' : '期限未設定';
}

function getDueStage(daysRemaining, status = 'todo') {
  if (String(status || '').trim().toLowerCase() === 'done') return 'done';
  if (!Number.isFinite(daysRemaining)) return 'none';
  if (daysRemaining < 0) return 'overdue';
  if (daysRemaining === 0) return 'today';
  if (daysRemaining === 1) return 'tomorrow';
  if (daysRemaining <= 3) return 'soon';
  if (daysRemaining <= 7) return 'week';
  if (daysRemaining <= 14) return 'upcoming';
  return 'future';
}

function formatDueCountdown(daysRemaining, stage) {
  if (!Number.isFinite(daysRemaining)) return '';
  if (stage === 'done') return '';
  if (daysRemaining < 0) return `${Math.abs(daysRemaining)}日超過`;
  if (daysRemaining === 0) return '今日まで';
  if (daysRemaining === 1) return '明日まで';
  if (daysRemaining >= 30) return '';
  return `残り${daysRemaining}日`;
}

export function getTaskDueMeta(taskOrDueOn, fallbackStatus = 'todo') {
  const taskLike =
    taskOrDueOn && typeof taskOrDueOn === 'object'
      ? taskOrDueOn
      : { dueOn: taskOrDueOn, status: fallbackStatus };
  const dueOn = taskLike?.dueOn || null;
  const dueDate = toDateOnly(dueOn);
  if (!dueDate) {
    return {
      hasDue: false,
      stage: 'none',
      dateLabel: '期限未設定',
      countdownLabel: '',
    };
  }

  const today = toDateOnly(toDateKey(new Date()));
  const daysRemaining = today ? diffDays(today, dueDate) : null;
  const stage = getDueStage(daysRemaining, taskLike?.status || fallbackStatus);
  return {
    hasDue: true,
    stage,
    dateLabel: formatDueLabel(dueOn),
    countdownLabel: formatDueCountdown(daysRemaining, stage),
  };
}

// 期限を小さなチップ向けに整形する。
// tone: 'overdue' | 'today' | 'soon' | 'none'
export function getDueChip(task) {
  const meta = getTaskDueMeta(task, task?.status || 'todo');
  if (!meta.hasDue) return { label: '', tone: 'none' };
  if (meta.stage === 'overdue') return { label: meta.countdownLabel || '超過', tone: 'overdue' };
  if (meta.stage === 'today') return { label: '今日', tone: 'today' };
  if (meta.stage === 'tomorrow') return { label: '明日', tone: 'soon' };
  return { label: meta.dateLabel, tone: 'soon' };
}

// 担当者の表示ラベル（管理者 / アーティスト / 自分）。
export function getQuickTaskAssigneeLabel(task) {
  const admins = Array.isArray(task?.assignees)
    ? task.assignees.map((a) => String(a?.displayName || '').trim()).filter(Boolean)
    : [];
  const artists = Array.isArray(task?.artistAssignees)
    ? task.artistAssignees.map((a) => String(a?.displayName || '').trim()).filter(Boolean)
    : [];
  const all = [...admins, ...artists];
  if (!all.length) return '自分';
  return all.join(' / ');
}

export function getQuickTaskAssignees(task) {
  const admins = Array.isArray(task?.assignees)
    ? task.assignees.map((item) => ({
        type: 'admin',
        id: item?.adminUserId || item?.admin_user_id || '',
        displayName: String(item?.displayName || '').trim(),
        profilePicture: String(item?.profilePicture || item?.profile_picture || '').trim(),
      }))
    : [];
  const artists = Array.isArray(task?.artistAssignees)
    ? task.artistAssignees.map((item) => ({
        type: 'artist',
        id: item?.artistId || item?.artist_id || '',
        displayName: String(item?.displayName || '').trim(),
        profilePicture: String(item?.profilePicture || item?.profile_picture || '').trim(),
      }))
    : [];
  return [...admins, ...artists].filter((item) => item.displayName || item.id);
}
